import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import Container from 'react-bootstrap/Container';
import membersApi from '../api/membersApi';
import MemberFormModal from './MemberFormModal';
import MembersPaginationControls from './MembersPaginationControls';
import MembersTable from './MembersTable';
import useMembers from '../hooks/useMembers';
import { useAuth } from '../contexts/AuthContext';
import AccessState from './AccessState';
import {
  canAccessMembersManagement,
} from '../utils/accessControl';

const readStoredNumber = (key) => {
  const raw = localStorage.getItem(key);
  if (raw === null || raw === undefined || raw === '') return null;

  const next = Number(raw);
  return Number.isFinite(next) ? next : null;
};

const normalizeAllowanceValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'allowed' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'not allowed' || normalized === 'no') return false;
  }
  return null;
};

const SupportMembers = () => {
  const pageSizeOptions = [25, 50, 100];
  const navigate = useNavigate();
  const msgs = useRef(null);
  const toast = useRef(null);

  const [search, setSearch] = useState('');
  const [locationId, setLocationId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const { accessContext, isAuthReady } = useAuth();

  const customerId = accessContext && accessContext.claims && accessContext.claims.customerId !== null && accessContext.claims.customerId !== undefined
    ? Number(accessContext.claims.customerId)
    : readStoredNumber('id');
  const appProviderId = accessContext && accessContext.claims && accessContext.claims.appProviderId !== null && accessContext.claims.appProviderId !== undefined
    ? accessContext.claims.appProviderId
    : readStoredNumber('appid');
  const hasAccess = canAccessMembersManagement(accessContext);

  const { members, loading, error, pagination, loadMembers, saveMember, deleteMember } = useMembers({
    scope: 'support',
    customerId,
    appProviderId,
    groupFilter: customerId,
    lockedGroupId: customerId,
  });

  const locationOptions = useMemo(
    () => [{ label: 'All Locations', value: null }, ...(locations || []).map((location) => ({ label: location.name, value: location.id }))],
    [locations]
  );

  const customerOptions = useMemo(
    () => (customerId !== null && customerId !== undefined ? [{ label: String(customerId), value: customerId }] : []),
    [customerId]
  );

  const currentPage = Number(pagination.page || page);
  const currentPageSize = Number(pagination.pageSize || pageSize);
  const totalCount = Number(pagination.totalCount || 0);
  const totalPages = Number(pagination.totalPages || 0);

  useEffect(() => {
    if (!customerId) return;

    const loadSupportLookups = async () => {
      try {
        setLocationsLoading(true);
        let appid = localStorage.getItem('appid');
        if (appProviderId !== null && appProviderId !== undefined && String(appProviderId) !== '') {
          appid = appProviderId;
        }
        const nextLocations = await membersApi.getSupportLocations(customerId, appid);
        setLocations(nextLocations);
      } catch (err) {
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: '', detail: 'Failed to load member locations.' });
        }
      } finally {
        setLocationsLoading(false);
      }
    };

    loadSupportLookups();
  }, [customerId, appProviderId]);

  useEffect(() => {
    setPage(0);
  }, [search, locationId]);

  useEffect(() => {
    if (!customerId || !hasAccess) return;

    loadMembers({
      search,
      locationId,
      page,
      pageSize,
    }).catch(() => {});
  }, [customerId, hasAccess, locationId, page, pageSize, search, loadMembers]);

  const handleCreate = () => {
    setSelectedMember(null);
    setModalVisible(true);
  };

  const handleEdit = async (member) => {
    try {
      const detail = await membersApi.getSupportMemberById(customerId, member.id, { appProviderId });
      setSelectedMember(detail);
      setModalVisible(true);
    } catch (err) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: 'Failed to load the selected member.' });
      }
    }
  };

  const handleDelete = async (member) => {
    const confirmed = window.confirm(`Delete member '${member.name || member.memberId || member.id}'?`);
    if (!confirmed) return;

    try {
      await deleteMember(member);
      const nextPage = currentPage > 0 && totalCount - 1 <= currentPage * currentPageSize
        ? currentPage - 1
        : currentPage;
      if (nextPage !== currentPage) {
        setPage(nextPage);
      } else {
        await loadMembers({ search, locationId, page: currentPage, pageSize: currentPageSize });
      }
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: 'Member deleted.' });
      }
    } catch (_) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: 'Failed to delete member.' });
      }
    }
  };

  const handleSave = async (member) => {
    const isUpdate = Boolean(member && member.id);
    const trimmedName = String(member && member.name ? member.name : '').trim();
    const trimmedSurname = String(member && member.surname ? member.surname : '').trim();
    const safeLocationId = member && member.locationId !== null && member.locationId !== undefined && member.locationId !== ''
      ? Number(member.locationId)
      : null;
    const safeAllowance = normalizeAllowanceValue(member && member.allowance);

    if (isUpdate && (!trimmedName || !trimmedSurname || safeAllowance === null)) {
      if (msgs.current) {
        msgs.current.show({ severity: 'warn', summary: '', detail: 'Name, surname, and allowance are required.' });
      }
      return;
    }

    if (!isUpdate && (!trimmedName || !trimmedSurname || safeLocationId === null || !Number.isFinite(safeLocationId))) {
      if (msgs.current) {
        msgs.current.show({ severity: 'warn', summary: '', detail: 'Name, surname, and location are required.' });
      }
      return;
    }

    try {
      await saveMember({
        ...member,
        name: trimmedName,
        surname: trimmedSurname,
        locationId: isUpdate ? member.locationId : safeLocationId,
        allowance: isUpdate ? safeAllowance : member.allowance,
        groupId: customerId,
      });
      setModalVisible(false);
      setSelectedMember(null);
      if (isUpdate) {
        await loadMembers({ search, locationId, page: currentPage, pageSize: currentPageSize });
      } else {
        setPage(0);
      }
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: isUpdate ? 'Member updated.' : 'Member created.' });
      }
    } catch (_) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: 'Failed to save member.' });
      }
    }
  };

  const clearFilters = () => {
    setSearch('');
    setLocationId(null);
  };

  const onPageChange = (event) => {
    if (Number(event.rows) !== Number(pageSize)) {
      setPageSize(event.rows);
      setPage(0);
      return;
    }

    setPage(Math.floor(event.first / event.rows));
  };

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
    </div>
  );

  const bootstrapping = !isAuthReady;

  if (bootstrapping) {
    return <Container fluid className="justify-content-md-center mt-5">{progressbar}</Container>;
  }

  if (!hasAccess) {
    return (
      <AccessState
        title="Members Unavailable"
        message="Your account does not have access to members management."
        actionLabel="Go To Support Home"
        actionTo="/support"
        autoRedirectTo="/support"
        autoRedirectDelayMs={2500}
      />
    );
  }

  if (!customerId) {
    return (
      <AccessState
        title="Customer Scope Required"
        message="Your account does not have a customer scope, so support members cannot be loaded."
        actionLabel="Go To Support Home"
        actionTo="/support"
        autoRedirectTo="/support"
        autoRedirectDelayMs={2500}
      />
    );
  }

  return (
    <Container fluid="md" className="justify-content-md-center mt-5">
      <div className="panel">
        <div className="flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div className="flex align-items-center gap-3 flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="p-0" />
            <h4 className="m-0">Support Members</h4>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search members" />
            </span>
            <Dropdown
              value={locationId}
              options={locationOptions}
              onChange={(e) => setLocationId(e.value)}
              placeholder="Filter by location"
              loading={locationsLoading}
              style={{ minWidth: '18rem' }}
            />
            <Button icon="pi pi-filter-slash" className="p-button-text" onClick={clearFilters} tooltip="Clear filters" tooltipOptions={{ position: 'bottom' }} />
            <Button icon="pi pi-refresh" className="p-button-text" onClick={() => loadMembers({ search, locationId, page, pageSize }).catch(() => {})} tooltip="Refresh members" tooltipOptions={{ position: 'bottom' }} />
          </div>
          <Button label="Create Member" icon="pi pi-plus" className="p-button-success" onClick={handleCreate} />
        </div>
      </div>

      <Messages ref={msgs} />
      <Toast ref={toast} />
      {(loading || locationsLoading) && progressbar}
      {error && <p>{error}</p>}

      <MembersTable
        members={members.filter((member) => Number(member.groupId) === customerId)}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage={loading ? 'Loading members...' : 'No members found for this customer.'}
        variant="support"
      />

      <MembersPaginationControls
        page={currentPage}
        pageSize={currentPageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        pageSizeOptions={pageSizeOptions}
        onPageChange={onPageChange}
      />

      <MemberFormModal
        visible={modalVisible}
        member={selectedMember}
        onHide={() => {
          setModalVisible(false);
          setSelectedMember(null);
        }}
        onSave={handleSave}
        saving={loading}
        customerOptions={customerOptions}
        locationOptions={(locations || []).map((location) => ({ label: location.name, value: location.id }))}
        defaultGroupId={customerId}
        lockGroupId
        minimalCreateMode
        supportRestrictedEdit
      />
    </Container>
  );
};

export default SupportMembers;
